import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { ColorPickerField } from '@/components/playlist/ColorPickerField';

import { Playlist, PlaylistTemplate, NetflixTemplateSettings, PlaylistAppearanceSettings } from '@/store/playlist/types';
import { Play, LayoutList, Tv2, Palette, Image as ImageIcon, Link2, User, FileText, X, AlertCircle, GraduationCap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { validateUrl } from '@/lib/security';
import { DarkMembersEditor } from './DarkMembersEditor';
import { handleImageUploadWithCompression } from '@/utils/compressedImageUpload';

interface PlaylistUnifiedEditorProps {
  playlist: Playlist;
  onUpdate: <K extends keyof Playlist>(key: K, value: Playlist[K]) => void;
}

const DEFAULT_HEADER_COLORS = ['#2D7BFF', '#7C3AED', '#EF4444', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6'];
const DEFAULT_ACCENT_COLORS = ['#2D7BFF', '#7C3AED', '#EF4444', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6'];

const DEFAULT_FOOTER_SETTINGS = {
  showTerms: false,
  termsUrl: '',
  showPrivacy: false,
  privacyUrl: '',
  showCustomText: false,
  customText: '',
  whatsapp: {
    enabled: false,
    number: '',
    message: '',
  },
};

export const PlaylistUnifiedEditor: React.FC<PlaylistUnifiedEditorProps> = ({
  playlist,
  onUpdate,
}) => {
  useLanguage();
  const [selectedTemplate, setSelectedTemplate] = useState<PlaylistTemplate>(playlist.template || 'clean');

  // Estados individuais para expansão no Rodapé
  const [expandedSections, setExpandedSections] = useState({
    terms: playlist.footerSettings?.showTerms ?? false,
    privacy: playlist.footerSettings?.showPrivacy ?? false,
    whatsapp: playlist.footerSettings?.whatsapp?.enabled ?? false,
    customText: playlist.footerSettings?.showCustomText ?? false,
  });

  // Estado para erros de validação de URLs
  const [urlErrors, setUrlErrors] = useState<{
    termsUrl?: string;
    privacyUrl?: string;
  }>({});

  // Sincronizar estados quando a playlist mudar
  useEffect(() => {
    setExpandedSections({
      terms: playlist.footerSettings?.showTerms ?? false,
      privacy: playlist.footerSettings?.showPrivacy ?? false,
      whatsapp: playlist.footerSettings?.whatsapp?.enabled ?? false,
      customText: playlist.footerSettings?.showCustomText ?? false,
    });
  }, [playlist.id, playlist.footerSettings]);

  const handleTemplateSelect = (template: PlaylistTemplate) => {
    setSelectedTemplate(template);
    onUpdate('template', template);
  };

  const handleUrlChange = (
    field: 'termsUrl' | 'privacyUrl',
    value: string
  ) => {
    // Limpa erro se campo vazio
    if (!value.trim()) {
      setUrlErrors(prev => ({ ...prev, [field]: undefined }));
      onUpdate('footerSettings', {
        ...DEFAULT_FOOTER_SETTINGS,
        ...playlist.footerSettings,
        [field]: value,
      });
      return;
    }

    // Valida URL
    const validation = validateUrl(value);
    
    if (!validation.isValid) {
      setUrlErrors(prev => ({ 
        ...prev, 
        [field]: translateInterfaceText("URL inválida. Use formato: https://exemplo.com")
      }));
    } else {
      setUrlErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Atualiza valor independente da validação
    onUpdate('footerSettings', {
      ...DEFAULT_FOOTER_SETTINGS,
      ...playlist.footerSettings,
      [field]: value,
    });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUploadWithCompression(
        file,
        (compressed) => onUpdate('customLogo', compressed),
        { maxWidth: 400, quality: 0.7 }
      );
    }
  };

  const handleFaviconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUploadWithCompression(
        file,
        (compressed) => onUpdate('favicon', compressed),
        { maxWidth: 256, quality: 0.75 }
      );
    }
  };

  const netflixSettings = selectedTemplate === 'netflix' ? (playlist.templateSettings as NetflixTemplateSettings) : undefined;

  const handleNetflixSettingChange = <K extends keyof NetflixTemplateSettings>(
    key: K,
    value: NetflixTemplateSettings[K]
  ) => {
    onUpdate('templateSettings', {
      ...netflixSettings,
      [key]: value,
    } as NetflixTemplateSettings);
  };

  const templates = [
    {
      id: 'clean' as PlaylistTemplate,
      name: 'Clean',
      description: 'Modelo minimalista com player em destaque.',
      icon: Play,
      preview: (
        <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
          <Play className="w-12 h-12 text-[#2D6CF6]/40" />
        </div>
      ),
    },
    {
      id: 'panda' as PlaylistTemplate,
      name: 'Light',
      description: 'Player grande + lista lateral. Layout limpo e profissional.',
      icon: LayoutList,
      preview: (
        <div className="h-32 bg-white rounded-lg border border-gray-200 p-2 flex gap-2">
          <div className="flex-1 bg-gradient-to-br from-gray-300 to-gray-400 rounded"></div>
          <div className="w-16 flex flex-col gap-1">
            <div className="w-full h-4 bg-gray-300 rounded"></div>
            <div className="w-full h-4 bg-gray-200 rounded"></div>
            <div className="w-full h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      ),
    },
    {
      id: 'dark-members' as PlaylistTemplate,
      name: 'Dark Members',
      description: 'Área de membros premium com capa hero, módulos em cards e aulas internas.',
      icon: User,
      preview: (
        <div className="h-32 bg-[#0E0E0E] rounded-lg p-3 flex flex-col gap-2">
          {/* Mini capa */}
          <div className="w-full h-10 bg-gradient-to-r from-[#1A1A1A] to-[#252525] rounded" />
          {/* Mini módulos */}
          <div className="flex gap-2">
            <div className="w-8 h-14 bg-gray-700 rounded" />
            <div className="w-8 h-14 bg-gray-700 rounded" />
            <div className="w-8 h-14 bg-gray-700 rounded" />
          </div>
        </div>
      ),
    },
    {
      id: 'netflix' as PlaylistTemplate,
      name: 'Dark Vídeos',
      description: 'Catálogo moderno com fundo escuro e carrossel horizontal.',
      icon: Tv2,
      preview: (
        <div className="h-32 bg-[#0D0D0D] rounded-lg p-3 flex flex-col gap-2">
          <div className="flex gap-2">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-700 to-gray-800 rounded"></div>
            <div className="w-16 h-16 bg-gradient-to-br from-gray-700 to-gray-800 rounded"></div>
            <div className="w-16 h-16 bg-gradient-to-br from-gray-700 to-gray-800 rounded"></div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Nome da Playlist */}
      <div className="mb-6" style={{ maxWidth: '420px' }}>
        {/* Label com ícone */}
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-[#2D6CF6]" />
          <label className="text-sm font-semibold text-[#111827]">
            {translateInterfaceText("Nome da Playlist")}</label>
        </div>
        
        {/* Input padrão Host VSL */}
        <Input
          value={playlist.name || ''}
          onChange={(e) => onUpdate('name', e.target.value)}
          placeholder={translateInterfaceText("Digite um nome para identificar sua playlist")}
          className="h-11 rounded-[10px] border-[#D1D5DB] hover:border-[#BFD9FF] focus:border-[#2D7BFF] focus:ring-[#2D7BFF]"
        />
        
        {/* Texto auxiliar */}
        <p className="text-xs text-[#6B7280] mt-1.5">
          {translateInterfaceText("Uso interno. Aparece no dashboard, não aparece no player público.")}</p>
      </div>

      {/* Template Selector */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2">
          <Palette className="w-5 h-5 text-[#2D6CF6]" />
          <h2 className="text-lg font-semibold">{translateInterfaceText("Escolha do Template")}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {templates.map((template) => {
            const Icon = template.icon;
            const isSelected = selectedTemplate === template.id;

            return (
              <div
                key={template.id}
                className={cn(
                  'group border-2 rounded-xl transition-all duration-200',
                  isSelected
                    ? 'border-[#2D6CF6] bg-[#E8F1FF]'
                    : 'border-[#E6ECF5] bg-white'
                )}
              >
                <div className="space-y-4 p-6">
                  {/* Preview */}
                  {template.preview}

                  {/* Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-[#2D6CF6]" />
                      <h3 className="font-semibold text-[15px]">{template.name}</h3>
                      {isSelected && (
                        <div className="px-2 py-0.5 rounded-full bg-[#2D6CF6] text-white text-[11px] font-medium">
                          {translateInterfaceText("Ativo")}</div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {template.description}
                    </p>
                  </div>

                  {/* Botões */}
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      onClick={() => handleTemplateSelect(template.id)}
                      className={cn(
                        "h-10 text-[14px] font-medium flex-[1.5] transition-all",
                        isSelected
                          ? "bg-[#2D6CF6] hover:bg-[#1F5ED6] text-white"
                          : "bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#111] border border-[#E6ECF5]"
                      )}
                    >
                      {isSelected ? translateInterfaceText("Selecionado") : translateInterfaceText("Selecionar")}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        const params = new URLSearchParams();
                        
                        if (playlist.publicTitle) params.append('title', playlist.publicTitle);
                        if (playlist.description) params.append('description', playlist.description);
                        if (playlist.name) params.append('name', playlist.name);
                        if (playlist.favicon) params.append('favicon', playlist.favicon);
                        
                        const baseUrl = playlist.id 
                          ? `/playlist/preview/${template.id}/${playlist.id}`
                          : `/playlist/preview/${template.id}`;
                          
                        const url = params.toString() 
                          ? `${baseUrl}?${params.toString()}`
                          : baseUrl;
                          
                        window.open(url, '_blank');
                      }}
                      variant="outline"
                      className="h-10 text-[14px] font-medium flex-1 border-[#D1D5DB] text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#111] hover:border-[#9CA3AF]"
                    >
                      {translateInterfaceText("Pré-visualizar")}</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cores do Tema - SÓ aparece para Prime Video Dark */}
      {selectedTemplate === 'netflix' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2">
            <Palette className="w-5 h-5 text-[#2D6CF6]" />
            <h2 className="text-lg font-semibold">{translateInterfaceText("Configurações Prime Video Dark")}</h2>
          </div>

          <div className="space-y-6">
            {/* Hero Banner Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#111827]">
                {translateInterfaceText("Imagem de Capa (1920×700px recomendado)")}</Label>
              <div className="border-2 border-dashed border-[#D1D5DB] rounded-lg p-6 text-center hover:border-[#2D7BFF] transition-colors">
                {playlist.primeVideoSettings?.heroImage ? (
                  <div className="relative">
                    <img 
                      src={playlist.primeVideoSettings.heroImage} 
                      alt={translateInterfaceText("Hero Banner")}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => onUpdate('primeVideoSettings', {
                        ...playlist.primeVideoSettings,
                        heroImage: undefined,
                      })}
                    >
                      {translateInterfaceText("Remover")}</Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <ImageIcon className="w-10 h-10 mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600">
                      {translateInterfaceText("Clique para fazer upload (1920×700px)")}</p>
                    <Input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="prime-hero-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleImageUploadWithCompression(
                            file,
                            (compressed) => {
                              onUpdate('primeVideoSettings', {
                                ...playlist.primeVideoSettings,
                                heroImage: compressed,
                                accentColor: playlist.primeVideoSettings?.accentColor || '#2D7BFF',
                                autoPlayNext: playlist.primeVideoSettings?.autoPlayNext || false,
                              });
                            },
                            { maxWidth: 1200, quality: 0.6 }
                          );
                        }
                      }}
                    />
                    <label 
                      htmlFor="prime-hero-upload"
                      className="inline-flex items-center justify-center h-10 px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] hover:border-[#3B82F6] cursor-pointer transition-colors"
                    >
                      {translateInterfaceText("Selecionar Imagem")}</label>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#6B7280]">
                {translateInterfaceText("Resolução recomendada: 1920×700 pixels")}</p>
            </div>


            {/* Auto-play Next */}
            <div className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-lg">
              <div className="space-y-1">
                <Label className="text-sm font-semibold text-[#111827]">
                  {translateInterfaceText("Auto-play Próxima Aula")}</Label>
                <p className="text-xs text-[#6B7280]">
                  {translateInterfaceText("Reproduzir automaticamente a próxima aula quando o vídeo terminar")}</p>
              </div>
              <Switch
                checked={playlist.primeVideoSettings?.autoPlayNext || false}
                onCheckedChange={(checked) =>
                  onUpdate('primeVideoSettings', {
                    ...playlist.primeVideoSettings,
                    heroImage: playlist.primeVideoSettings?.heroImage,
                    accentColor: playlist.primeVideoSettings?.accentColor || '#2D7BFF',
                    autoPlayNext: checked,
                  })
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Configurações Dark Members (só aparece se template === 'dark-members') */}
      {selectedTemplate === 'dark-members' && (
        <DarkMembersEditor 
          playlist={playlist}
          onUpdate={onUpdate}
        />
      )}

      {/* Identidade Visual */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2">
          <ImageIcon className="w-5 h-5 text-[#2D6CF6]" />
          <h2 className="text-lg font-semibold">{translateInterfaceText("Identidade Visual")}</h2>
        </div>

        {/* LINHA 1: Título e Descrição - NÃO aparece para Netflix Dark */}
        {selectedTemplate !== 'netflix' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {/* Título da Playlist (público) - ESQUERDA */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{translateInterfaceText("Título da Playlist")}</Label>
              <Input
                value={playlist.publicTitle || ''}
                onChange={(e) => onUpdate('publicTitle', e.target.value)}
                placeholder={translateInterfaceText("Ex: Curso Completo de Marketing")}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              {translateInterfaceText("Aparece no header e sidebar do player público")}</p>
          </div>

          {/* Descrição da Playlist (público) - DIREITA */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{translateInterfaceText("Descrição da Playlist")}</Label>
            <Input
              value={playlist.description || ''}
              onChange={(e) => onUpdate('description', e.target.value)}
              placeholder={translateInterfaceText("Aprenda as melhores estratégias de marketing digital")}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              {translateInterfaceText("Aparece na sidebar do player público")}</p>
            </div>
          </div>
        )}

        {/* LINHA 2: Favicon (e Logo se não for Netflix) */}
        <div className={cn(
          "gap-6 items-start mt-6",
          selectedTemplate === 'netflix' 
            ? "flex flex-col max-w-md" 
            : "grid grid-cols-1 md:grid-cols-2"
        )}>
          {/* Favicon - ESQUERDA */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">{translateInterfaceText("Favicon")}</Label>
            <div className="relative">
              <input
                type="file"
                accept="image/png"
                onChange={handleFaviconUpload}
                className="hidden"
                id="favicon-upload"
              />
              <label
                htmlFor="favicon-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-[#2D6CF6] transition-colors"
              >
                {playlist.favicon ? (
                  <img
                    src={playlist.favicon}
                    alt={translateInterfaceText("Favicon")}
                    className="max-w-full max-h-full object-contain p-2"
                  />
                ) : (
                  <div className="text-center">
                    <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">{translateInterfaceText("Clique para fazer upload")}</p>
                  </div>
                )}
              </label>
              
              {/* Botão Remover */}
              {playlist.favicon && (
                <button
                  type="button"
                  onClick={() => onUpdate('favicon', undefined)}
                  className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{translateInterfaceText("PNG 256×256px recomendado")}</p>
          </div>

          {/* Logo da Playlist - DIREITA - NÃO aparece para Netflix Dark */}
          {selectedTemplate !== 'netflix' && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">{translateInterfaceText("Logo da Playlist")}</Label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-[#2D6CF6] transition-colors"
                >
                  {playlist.customLogo ? (
                    <img
                      src={playlist.customLogo}
                      alt={translateInterfaceText("Logo")}
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  ) : (
                    <div className="text-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">{translateInterfaceText("Clique para fazer upload")}</p>
                    </div>
                  )}
                </label>
                
                {/* Botão Remover */}
                {playlist.customLogo && (
                  <button
                    type="button"
                    onClick={() => onUpdate('customLogo', undefined)}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{translateInterfaceText("PNG recomendado")}</p>
            </div>
          )}
        </div>
      </div>


      {/* Rodapé */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2">
          <FileText className="w-5 h-5 text-[#2D6CF6]" />
          <h2 className="text-lg font-semibold">{translateInterfaceText("Rodapé")}</h2>
        </div>

        {/* Grid 2x2 para toggles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {/* Termos de Uso */}
          <div className="space-y-3 p-4 border rounded-xl bg-muted/30 self-start">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{translateInterfaceText("Termos de Uso")}</Label>
            <Switch
              checked={playlist.footerSettings?.showTerms ?? false}
              onCheckedChange={(checked) => {
                setExpandedSections(prev => ({ ...prev, terms: checked }));
                onUpdate('footerSettings', {
                  ...DEFAULT_FOOTER_SETTINGS,
                  ...playlist.footerSettings,
                  showTerms: checked,
                });
              }}
            />
          </div>
          {expandedSections.terms && (
              <div className="space-y-1">
                <Input
                  value={playlist.footerSettings?.termsUrl || ''}
                  onChange={(e) => handleUrlChange('termsUrl', e.target.value)}
                  placeholder="https://seusite.com/termos"
                  className={cn(
                    "w-full animate-in fade-in-0 slide-in-from-top-2",
                    urlErrors.termsUrl && "border-red-500 focus-visible:ring-red-500"
                  )}
                />
                {urlErrors.termsUrl && (
                  <p className="text-xs text-red-500 animate-in fade-in-0">
                    {urlErrors.termsUrl}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Política de Privacidade */}
          <div className="space-y-3 p-4 border rounded-xl bg-muted/30 self-start">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{translateInterfaceText("Política de Privacidade")}</Label>
            <Switch
              checked={playlist.footerSettings?.showPrivacy ?? false}
              onCheckedChange={(checked) => {
                setExpandedSections(prev => ({ ...prev, privacy: checked }));
                onUpdate('footerSettings', {
                  ...DEFAULT_FOOTER_SETTINGS,
                  ...playlist.footerSettings,
                  showPrivacy: checked,
                });
              }}
            />
          </div>
          {expandedSections.privacy && (
              <div className="space-y-1">
                <Input
                  value={playlist.footerSettings?.privacyUrl || ''}
                  onChange={(e) => handleUrlChange('privacyUrl', e.target.value)}
                  placeholder="https://seusite.com/privacidade"
                  className={cn(
                    "w-full animate-in fade-in-0 slide-in-from-top-2",
                    urlErrors.privacyUrl && "border-red-500 focus-visible:ring-red-500"
                  )}
                />
                {urlErrors.privacyUrl && (
                  <p className="text-xs text-red-500 animate-in fade-in-0">
                    {urlErrors.privacyUrl}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Botão WhatsApp */}
          <div className="space-y-3 p-4 border rounded-xl bg-muted/30 self-start">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{translateInterfaceText("Botão WhatsApp")}</Label>
            <Switch
              checked={playlist.footerSettings?.whatsapp?.enabled ?? false}
              onCheckedChange={(checked) => {
                setExpandedSections(prev => ({ ...prev, whatsapp: checked }));
                onUpdate('footerSettings', {
                  ...DEFAULT_FOOTER_SETTINGS,
                  ...playlist.footerSettings,
                  whatsapp: {
                    ...DEFAULT_FOOTER_SETTINGS.whatsapp,
                    ...playlist.footerSettings?.whatsapp,
                    enabled: checked,
                  },
                });
              }}
            />
          </div>
          
          {expandedSections.whatsapp && (
              <div className="space-y-4 animate-in fade-in-0 slide-in-from-top-2">
                {/* Número do WhatsApp */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium">{translateInterfaceText("Número do WhatsApp")}</Label>
                  <Input
                    value={playlist.footerSettings?.whatsapp?.number || ''}
                    onChange={(e) => {
                      // Aceita apenas números, espaços, +, parênteses e traços
                      const value = e.target.value.replace(/[^\d\s+()-]/g, '');
                      onUpdate('footerSettings', {
                        ...DEFAULT_FOOTER_SETTINGS,
                        ...playlist.footerSettings,
                        whatsapp: {
                          ...DEFAULT_FOOTER_SETTINGS.whatsapp,
                          ...playlist.footerSettings?.whatsapp,
                          number: value,
                        },
                      });
                    }}
                    placeholder={translateInterfaceText("Ex: 71 99999-8888")}
                    maxLength={20}
                    className="w-full"
                  />
                </div>
                
                {/* Mensagem automática (opcional) */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium">{translateInterfaceText("Mensagem automática (opcional)")}</Label>
                  <Input
                    value={playlist.footerSettings?.whatsapp?.message || ''}
                    onChange={(e) =>
                      onUpdate('footerSettings', {
                        ...DEFAULT_FOOTER_SETTINGS,
                        ...playlist.footerSettings,
                        whatsapp: {
                          ...DEFAULT_FOOTER_SETTINGS.whatsapp,
                          ...playlist.footerSettings?.whatsapp,
                          message: e.target.value,
                        },
                      })
                    }
                    placeholder={translateInterfaceText("Ex: Olá! Tenho interesse no conteúdo da sua playlist.")}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Texto Personalizado */}
          <div className="space-y-3 p-4 border rounded-xl bg-muted/30 self-start">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{translateInterfaceText("Texto Personalizado")}</Label>
            <Switch
              checked={playlist.footerSettings?.showCustomText ?? false}
              onCheckedChange={(checked) => {
                setExpandedSections(prev => ({ ...prev, customText: checked }));
                onUpdate('footerSettings', {
                  ...DEFAULT_FOOTER_SETTINGS,
                  ...playlist.footerSettings,
                  showCustomText: checked,
                });
              }}
            />
          </div>
          {expandedSections.customText && (
              <Textarea
                value={playlist.footerSettings?.customText || ''}
                onChange={(e) =>
                  onUpdate('footerSettings', {
                    ...DEFAULT_FOOTER_SETTINGS,
                    ...playlist.footerSettings,
                    customText: e.target.value,
                  })
                }
                placeholder={translateInterfaceText("Texto personalizado...")}
                className="w-full min-h-[80px] resize-none animate-in fade-in-0 slide-in-from-top-2"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
